import React, { useCallback, useEffect, useState } from "react";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import { toast } from "react-toastify";
import axios from "../../api/httpClient";
import { getApiBaseUrl } from "../../data/authStorage";

const profileFields = [
  { key: "firstName", label: "First name" },
  { key: "lastName", label: "Last name" },
  { key: "personalEmail", label: "Personal email", type: "email" },
  { key: "officialEmail", label: "Official email", type: "email" },
  { key: "phone", label: "Phone number", type: "tel" },
  { key: "gender", label: "Gender", options: ["Male", "Female", "Non-binary", "Prefer not to say"] },
  { key: "maritalStatus", label: "Marital status", options: ["Single", "Married", "Divorced", "Widowed", "Separated"] },
  { key: "dateOfBirth", label: "Date of birth", type: "date" },
  { key: "residentialAddress", label: "Residential address", multiline: true },
  { key: "lga", label: "Local government area" },
  { key: "stateOrigin", label: "State of origin" },
  { key: "stateResidence", label: "State of residence" },
  { key: "pensionProvider", label: "Pension provider" },
  { key: "pensionNumber", label: "Pension number" },
  { key: "hmo", label: "HMO" },
  { key: "hmoId", label: "HMO ID" },
  { key: "hmoNumber", label: "HMO number" },
  { key: "hmoStatus", label: "HMO status" },
  { key: "hmoPlan", label: "HMO plan" },
  { key: "hmoHospital", label: "HMO hospital" },
];

const recordGroups = [
  { key: "educationRecords", label: "Education", fields: ["school", "degree", "course", "qualificationClass", "startYear", "endYear"] },
  { key: "experienceRecords", label: "Experience", fields: ["company", "position", "employmentType", "start", "end", "description", "portfolioLink"] },
  { key: "awardsRecords", label: "Awards and certifications", fields: ["nameOfAward", "issuedBy", "awardDate", "expiryDate", "certification", "certificationBody", "certificateExpiryDate"] },
];

const titleCase = (value) => value.replace(/([A-Z])/g, " $1").replace(/^./, (letter) => letter.toUpperCase());
const dateValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? `${value}`.slice(0, 10) : date.toISOString().slice(0, 10);
};
const draftFromEmployee = (employee) => ({
  ...Object.fromEntries(profileFields.map(({ key }) => [key, key === "dateOfBirth" ? dateValue(employee?.dateOfBirth) : employee?.[key] || ""])),
  ...Object.fromEntries(recordGroups.map(({ key }) => [key, Array.isArray(employee?.[key]) ? employee[key] : []])),
});

function RecordEditor({ group, records, onChange }) {
  return (
    <fieldset className="rounded-lg border border-slate-200 p-3">
      <legend className="px-1 text-sm font-semibold text-[#071C50]">{group.label}</legend>
      <div className="space-y-3">
        {records.map((record, index) => (
          <div key={`${group.key}-${index}`} className="rounded-lg bg-slate-50 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500">Record {index + 1}</span>
              <button type="button" title={`Remove ${group.label.toLowerCase()} record`} onClick={() => onChange(records.filter((_, itemIndex) => itemIndex !== index))} className="rounded p-1 text-red-600 hover:bg-red-50"><FiTrash2 /></button>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {group.fields.map((field) => (
                <label key={field} className={field === "description" ? "sm:col-span-2" : ""}>
                  <span className="mb-1 block text-xs text-slate-600">{titleCase(field)}</span>
                  {field === "description" ? (
                    <textarea rows={2} value={record[field] || ""} onChange={(event) => onChange(records.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: event.target.value } : item))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" />
                  ) : (
                    <input type={field.toLowerCase().includes("date") || field === "start" || field === "end" ? "date" : "text"} value={field.toLowerCase().includes("date") || field === "start" || field === "end" ? dateValue(record[field]) : record[field] || ""} onChange={(event) => onChange(records.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: event.target.value } : item))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" />
                  )}
                </label>
              ))}
            </div>
          </div>
        ))}
        <button type="button" onClick={() => onChange([...records, {}])} className="inline-flex items-center gap-2 rounded-lg border border-[#1234CD] px-3 py-2 text-xs font-semibold text-[#1234CD]"><FiPlus />Add record</button>
      </div>
    </fieldset>
  );
}

export default function ProfileChangeRequestPanel({ employee }) {
  const apiBaseUrl = getApiBaseUrl();
  const [requests, setRequests] = useState([]);
  const [draft, setDraft] = useState(() => draftFromEmployee(employee));
  const [reason, setReason] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const loadRequests = useCallback(async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/organizations/employees/me/profile-change-requests`);
      setRequests(response?.data?.requests || []);
    } catch {
      setError("Unable to load your update request history.");
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests, employee?.id]);

  useEffect(() => {
    setDraft(draftFromEmployee(employee));
  }, [employee]);

  const pendingRequest = requests.find((request) => request.status === "Pending");
  const setRecordGroup = (key, value) => setDraft((previous) => ({ ...previous, [key]: value }));

  const submitRequest = async (event) => {
    event.preventDefault();
    const requestedChanges = {};
    profileFields.forEach(({ key }) => {
      const next = key === "dateOfBirth" ? draft[key] || null : `${draft[key] || ""}`.trim() || null;
      const current = key === "dateOfBirth" ? dateValue(employee?.dateOfBirth) || null : `${employee?.[key] || ""}`.trim() || null;
      if (next !== current) requestedChanges[key] = next;
    });
    recordGroups.forEach(({ key }) => {
      const current = Array.isArray(employee?.[key]) ? employee[key] : [];
      if (JSON.stringify(draft[key] || []) !== JSON.stringify(current)) requestedChanges[key] = draft[key] || [];
    });
    if (!Object.keys(requestedChanges).length) return toast.error("Change at least one profile detail before submitting.");
    if (!reason.trim()) return toast.error("Add a reason for the requested changes.");

    try {
      setSubmitting(true);
      await axios.post(`${apiBaseUrl}/organizations/employees/me/profile-change-requests`, { requestedChanges, reason: reason.trim() });
      toast.success("Your profile update request was sent for review.");
      setReason("");
      setOpen(false);
      await loadRequests();
    } catch (requestError) {
      const message = requestError?.response?.data?.message || "Unable to submit your profile update request.";
      toast.error(Array.isArray(message) ? message.join(", ") : message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mt-5 rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-[#071C50]">Profile detail changes</h3>
          <p className="mt-1 text-xs text-slate-500">Submit personal, contact, address, benefits, or record updates for approval.</p>
        </div>
        <button type="button" disabled={Boolean(pendingRequest)} onClick={() => { setDraft(draftFromEmployee(employee)); setOpen(true); }} className="rounded-lg bg-[#1234CD] px-3 py-2 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
          {pendingRequest ? "Request awaiting review" : "Request an update"}
        </button>
      </div>
      {error ? <p className="mt-3 text-xs text-red-600" role="alert">{error}</p> : null}
      {loading ? <p className="mt-3 text-xs text-slate-500">Loading request history...</p> : requests.length ? (
        <div className="mt-4 space-y-2">
          <p className="text-xs font-semibold text-slate-500">Recent requests</p>
          {requests.slice(0, 5).map((request) => (
            <article key={request.id} className="flex flex-wrap items-start justify-between gap-2 rounded-lg bg-slate-50 p-3">
              <div className="min-w-0"><p className="text-xs font-semibold text-slate-700">{Object.keys(request.requestedChanges || {}).map((key) => titleCase(key)).join(", ")}</p><p className="mt-1 text-[11px] text-slate-500">{request.reason}</p>{request.reviewNote ? <p className="mt-1 text-[11px] text-slate-500">Review note: {request.reviewNote}</p> : null}</div>
              <span className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-semibold ${request.status === "Approved" ? "bg-emerald-100 text-emerald-700" : request.status === "Rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{request.status}</span>
            </article>
          ))}
        </div>
      ) : <p className="mt-3 text-xs text-slate-500">No profile update requests yet.</p>}

      {open ? (
        <div className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-950/50 p-0 sm:items-center sm:p-4" onMouseDown={() => !submitting && setOpen(false)}>
          <div role="dialog" aria-modal="true" aria-labelledby="profile-change-title" className="max-h-[94vh] w-full max-w-3xl overflow-y-auto rounded-t-2xl bg-white p-4 shadow-2xl sm:rounded-2xl sm:p-6" onMouseDown={(event) => event.stopPropagation()}>
            <div className="mb-4 flex items-start justify-between gap-3"><div><h2 id="profile-change-title" className="text-lg font-bold text-[#071C50]">Request profile updates</h2><p className="mt-1 text-xs text-slate-500">Your existing details stay unchanged until an administrator approves this request.</p></div><button type="button" onClick={() => setOpen(false)} aria-label="Close" className="rounded-lg border px-3 py-1 text-sm">Close</button></div>
            <form onSubmit={submitRequest} className="space-y-5">
              <div className="grid gap-3 sm:grid-cols-2">
                {profileFields.map((field) => (
                  <label key={field.key} className={field.multiline ? "sm:col-span-2" : ""}>
                    <span className="mb-1 block text-xs font-semibold text-slate-600">{field.label}</span>
                    {field.options ? <select value={draft[field.key] || ""} onChange={(event) => setDraft((previous) => ({ ...previous, [field.key]: event.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"><option value="">Select {field.label.toLowerCase()}</option>{field.options.map((option) => <option key={option} value={option}>{option}</option>)}{draft[field.key] && !field.options.includes(draft[field.key]) ? <option value={draft[field.key]}>{draft[field.key]}</option> : null}</select> : field.multiline ? <textarea rows={3} value={draft[field.key] || ""} onChange={(event) => setDraft((previous) => ({ ...previous, [field.key]: event.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /> : <input type={field.type || "text"} value={draft[field.key] || ""} onChange={(event) => setDraft((previous) => ({ ...previous, [field.key]: event.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />}
                  </label>
                ))}
              </div>
              {recordGroups.map((group) => <RecordEditor key={group.key} group={group} records={draft[group.key] || []} onChange={(records) => setRecordGroup(group.key, records)} />)}
              <label className="block"><span className="mb-1 block text-xs font-semibold text-slate-600">Reason for this request</span><textarea required maxLength={2000} rows={3} value={reason} onChange={(event) => setReason(event.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="Briefly explain why these details need to change" /></label>
              <div className="flex justify-end gap-2"><button type="button" onClick={() => setOpen(false)} className="rounded-lg border px-4 py-2 text-sm font-semibold text-slate-600">Cancel</button><button type="submit" disabled={submitting} className="rounded-lg bg-[#1234CD] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{submitting ? "Submitting..." : "Submit for approval"}</button></div>
            </form>
          </div>
        </div>
      ) : null}
    </section>
  );
}